import { activityTypes, isActivityType, type ActivityType } from './activities';

export const MAX_EQUIPPED_GEAR = 5;
export interface WeightedOption<T> { value: T; weight: number }
export interface GearActivityBonus { category: 'activity-workout-points'; activity: ActivityType; points: number }
export type GearBonus = GearActivityBonus | { category: 'activity-workout-points-bundle'; bonuses: GearActivityBonus[] };
export type GearQuality = 'basic' | 'standard' | 'premium';
export interface GearRollCategory {
  category: GearActivityBonus['category']; weight: number;
  activities: readonly WeightedOption<ActivityType>[];
  strengths: readonly WeightedOption<number>[];
}
export interface GearDefinition {
  id: string; familyId: string; name: string; image: string;
  price: number; rollVersion: number; categories: readonly GearRollCategory[];
  quality: GearQuality; favoredActivity: ActivityType; bonusCounts: readonly WeightedOption<number>[];
}
export interface GearInstance {
  id: string; gearId: string; familyId: string; bonus: GearBonus; purchasedAt: string; rollVersion: number;
}
export interface UserGear {
  userId: string; catalog: readonly GearDefinition[]; items: GearInstance[]; equippedInstanceIds: string[];
}
export interface ShopData { userId: string; catalog: readonly GearDefinition[]; gold: number }
export interface PurchaseReceipt {
  purchaseId: string; gearId: string; instanceId: string; bonus: GearBonus;
  goldSpent: number; outcome: 'purchased' | 'duplicate'; purchasedAt: string;
}
export interface PurchaseResult { userId: string; receipt: PurchaseReceipt; gold: number; replayed: boolean }

const qualitySettings = {
  basic: { price: 150, counts: [85, 13, 2] },
  standard: { price: 350, counts: [70, 25, 5] },
  premium: { price: 750, counts: [55, 35, 10] },
} as const;

// Each family has one theme; artwork variants can change the full activity pool.
const familyGroups: Array<[ActivityType, GearQuality, string[]]> = [
  ['Pushups', 'basic', ['headband', 'wristbands']],
  ['Pushups', 'standard', ['athletic-wraps', 'athletic-gloves', 'protein-shaker']],
  ['Pushups', 'premium', ['flexed-arm-necklace']],
  ['Pull Ups', 'basic', ['tape-wraps']],
  ['Pull Ups', 'standard', ['fingerless-gloves', 'resistance-bands', 'leather-gloves']],
  ['Pull Ups', 'premium', ['gold-chain']],
  ['Squats', 'basic', ['casual-shoes', 'sandals']],
  ['Squats', 'standard', ['aerobics-step', 'gym-bag', 'ankle-brace']],
  ['Squats', 'premium', ['kettlebell', 'five-toe-shoes']],
  ['Burpees', 'basic', ['bandana', 'visor']],
  ['Burpees', 'standard', ['cooling-towel']],
  ['Burpees', 'premium', ['running-shoes', 'medal']],
  ['Sit-ups', 'basic', ['bubble-gum', 'puka-necklace']],
  ['Sit-ups', 'standard', ['medicine-ball', 'flat-yoga-mat']],
  ['Sit-ups', 'premium', ['dress-watch']],
  ['Jumping Jacks', 'basic', ['baseball-cap', 'sunglasses']],
  ['Jumping Jacks', 'standard', ['jump-rope', 'retro-headphones', 'high-top-sneakers', 'coach-whistle']],
  ['Jumping Jacks', 'premium', ['cycling-sunglasses', 'fitness-tracker']],
  ['Plank', 'basic', ['water-bottle', 'puka-anklet']],
  ['Plank', 'standard', ['rolled-yoga-mat', 'foam-roller', 'stopwatch']],
  ['Plank', 'premium', ['workout-watch', 'balance-board', 'digital-stopwatch']],
  ['Hang', 'basic', ['beanie', 'sunflower-seeds']],
  ['Hang', 'standard', ['hand-gripper']],
  ['Hang', 'premium', ['over-ear-headphones']],
];
// Remove the family favorite before selecting the two color preferences.
const colorThemes = {
  red: ['Burpees', 'Jumping Jacks', 'Pushups'],
  orange: ['Pushups', 'Squats', 'Pull Ups'],
  yellow: ['Squats', 'Jumping Jacks', 'Pushups'],
  green: ['Pull Ups', 'Hang', 'Squats'],
  teal: ['Sit-ups', 'Plank', 'Hang'],
  blue: ['Jumping Jacks', 'Squats', 'Burpees'],
  purple: ['Plank', 'Sit-ups', 'Pull Ups'],
  black: ['Hang', 'Pushups', 'Pull Ups'],
  white: ['Sit-ups', 'Pushups', 'Plank'],
} as const satisfies Record<string, readonly [ActivityType, ActivityType, ActivityType]>;
type GearColor = keyof typeof colorThemes;
type GearRegistration = [id: string, familyId: string, name: string, color?: GearColor];

const specialThemes: Record<string, readonly [ActivityType, ActivityType]> = {
  'wristbands-red-white-blue': ['Jumping Jacks', 'Burpees'],
  stopwatch: ['Hang', 'Sit-ups'],
};

function colorways(stem: string, familyId: string, name: string,
  colors: readonly GearColor[] = ['red', 'orange', 'yellow', 'green', 'teal', 'blue', 'purple', 'black', 'white'],
): GearRegistration[] {
  return colors.map((color) => [`${stem}-${color}`, familyId, `${color[0].toUpperCase() + color.slice(1)} ${name}`, color]);
}

// Explicit registrations: adding artwork alone must not silently create a shop offer.
const definitions: GearRegistration[] = [
  ...colorways('headband', 'headband', 'Headband', ['blue', 'red', 'orange', 'yellow', 'green', 'teal', 'purple', 'black', 'white']),
  ...colorways('bandana', 'bandana', 'Bandana'),
  ...colorways('baseball-cap', 'baseball-cap', 'Baseball Cap'),
  ...colorways('beanie', 'beanie', 'Beanie'),
  ['visor-backwards-red', 'visor', 'Backwards Red Visor', 'red'],
  ['sunglasses-black', 'sunglasses', 'Black Sunglasses', 'black'],
  ['sunglasses-cycling-shield', 'cycling-sunglasses', 'Cycling Shield Sunglasses'],
  ['necklace-puka-shell', 'puka-necklace', 'Puka Shell Necklace'],
  ['necklace-gold-chain', 'gold-chain', 'Gold Chain'],
  ['necklace-flexed-arm-gold', 'flexed-arm-necklace', 'Golden Flex Necklace'],
  ...colorways('cooling-towel', 'cooling-towel', 'Cooling Towel'),
  ['bubble-gum-pink', 'bubble-gum', 'Pink Bubble Gum'],
  ['sunflower-seeds', 'sunflower-seeds', 'Sunflower Seeds'],
  ['coach-whistle', 'coach-whistle', 'Coach’s Whistle'],
  ['headphones-over-ear-blue', 'over-ear-headphones', 'Blue Over-Ear Headphones', 'blue'],
  ['headphones-retro-foam', 'retro-headphones', 'Retro Foam Headphones'],
  ['athletic-medal', 'medal', 'Finisher’s Medal'],
  ...colorways('arm-wraps-tape', 'tape-wraps', 'Tape Wraps'),
  ['arm-wraps-athletic-blue', 'athletic-wraps', 'Blue Athletic Wraps', 'blue'],
  ['gloves-athletic-pair-blue', 'athletic-gloves', 'Blue Athletic Gloves', 'blue'],
  ['gloves-leather-brown', 'leather-gloves', 'Brown Leather Gloves'],
  ['gloves-fingerless-red', 'fingerless-gloves', 'Red Fingerless Gloves', 'red'],
  ['wristbands-red-white-blue', 'wristbands', 'Striped Wristbands'],
  ...colorways('wristbands', 'wristbands', 'Wristbands'),
  ['watch-workout-blue', 'workout-watch', 'Blue Workout Watch', 'blue'],
  ['watch-fitness-tracker-purple', 'fitness-tracker', 'Purple Fitness Tracker', 'purple'],
  ['watch-dress-gold-silver', 'dress-watch', 'Gold and Silver Watch'],
  ...colorways('water-bottle', 'water-bottle', 'Water Bottle'),
  ['protein-shaker', 'protein-shaker', 'Protein Shaker'],
  ...colorways('gym-bag', 'gym-bag', 'Gym Bag'),
  ['jump-rope', 'jump-rope', 'Jump Rope'],
  ...colorways('yoga-mat-rolled', 'rolled-yoga-mat', 'Yoga Mat'),
  ...colorways('foam-roller', 'foam-roller', 'Foam Roller'),
  ['resistance-bands', 'resistance-bands', 'Resistance Bands'],
  ['kettlebell', 'kettlebell', 'Kettlebell'],
  ['medicine-ball-red', 'medicine-ball', 'Red Medicine Ball', 'red'],
  ['hand-gripper', 'hand-gripper', 'Hand Gripper'],
  ['stopwatch', 'stopwatch', 'Classic Stopwatch'],
  ['stopwatch-white-face', 'stopwatch', 'White-Face Stopwatch', 'white'],
  ['stopwatch-digital', 'digital-stopwatch', 'Digital Stopwatch'],
  ['aerobics-step-blue', 'aerobics-step', 'Blue Aerobics Step', 'blue'],
  ['ankle-brace-blue', 'ankle-brace', 'Blue Ankle Brace', 'blue'],
  ['balance-board', 'balance-board', 'Balance Board'],
  ['sandals-teal', 'sandals', 'Teal Sandals', 'teal'],
  ['shoes-five-toe-blue', 'five-toe-shoes', 'Blue Five-Toe Shoes', 'blue'],
  ...colorways('yoga-mat-flat', 'flat-yoga-mat', 'Exercise Mat'),
  ['anklet-puka-shell', 'puka-anklet', 'Puka Shell Anklet'],
  ...colorways('running-shoes', 'running-shoes', 'Running Shoes'),
  ['casual-shoes-cream', 'casual-shoes', 'Cream Casual Shoes'],
  ...colorways('casual-shoes', 'casual-shoes', 'Casual Shoes'),
  ...colorways('sneakers-high-top', 'high-top-sneakers', 'High-Top Sneakers'),
];
export const gearCatalog: readonly GearDefinition[] = definitions.map(([id, familyId, name, color]) => {
  const group = familyGroups.find(([, , families]) => families.includes(familyId));
  if (!group) throw new Error(`Missing gear family balance: ${familyId}`);
  const [favoredActivity, quality] = group;
  const settings = qualitySettings[quality];
  const preferences = color ? colorThemes[color] : specialThemes[id];
  const variant = preferences?.filter((activity) => activity !== favoredActivity).slice(0, 2);
  return {
    id, familyId, name, image: `/public/assets/images/gear/icon-${id}.png`,
    quality, favoredActivity, price: settings.price, rollVersion: 3,
    bonusCounts: settings.counts.map((weight, index) => ({ value: index + 1, weight })),
    categories: [{
      category: 'activity-workout-points', weight: 1,
      activities: activityTypes.map((value) => ({ value, weight: variant
        ? value === favoredActivity ? 50 : value === variant[0] ? 20 : value === variant[1] ? 10 : 4
        : value === favoredActivity ? 65 : 5 })),
      strengths: Array.from({ length: 10 }, (_, index) => ({ value: index + 1,
        weight: quality === 'basic' ? 10 - index : quality === 'premium' ? index + 1 : 1 })),
    }],
  };
});

export function pickWeighted<T>(options: readonly WeightedOption<T>[], randomInt: (max: number) => number): T {
  if (!options.length || options.some((option) => !Number.isSafeInteger(option.weight) || option.weight <= 0)) throw new Error('Invalid gear roll weights');
  const total = options.reduce((sum, option) => sum + option.weight, 0);
  if (!Number.isSafeInteger(total)) throw new Error('Invalid gear roll total');
  let roll = randomInt(total);
  if (!Number.isInteger(roll) || roll < 0 || roll >= total) throw new Error('Invalid random roll');
  for (const option of options) {
    if (roll < option.weight) return option.value;
    roll -= option.weight;
  }
  throw new Error('Invalid gear roll');
}
export function rollGearBonus(item: GearDefinition, randomInt: (max: number) => number): GearBonus {
  const count = pickWeighted(item.bonusCounts, randomInt);
  const category = pickWeighted(item.categories.map((value) => ({ value, weight: value.weight })), randomInt);
  if (!Number.isInteger(count) || count < 1 || count > 3 || count > category.activities.length) throw new Error('Invalid gear bonus count');
  let remaining = [...category.activities];
  const bonuses: GearActivityBonus[] = [];
  for (let index = 0; index < count; index++) {
    const activity = pickWeighted(remaining, randomInt);
    remaining = remaining.filter((option) => option.value !== activity);
    bonuses.push({ category: category.category, activity, points: pickWeighted(category.strengths, randomInt) });
  }
  return bonuses.length === 1 ? bonuses[0] : {
    category: 'activity-workout-points-bundle',
    bonuses: bonuses.sort((a, b) => activityTypes.indexOf(a.activity) - activityTypes.indexOf(b.activity)),
  };
}
export function getGearBonuses(bonus: GearBonus): readonly GearActivityBonus[] {
  return bonus.category === 'activity-workout-points-bundle' ? bonus.bonuses : [bonus];
}

/** Validate historical stats without imposing today's strength distribution. */
export function isGearBonus(value: unknown): value is GearBonus {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const bonus = value as Partial<GearActivityBonus> & { bonuses?: unknown };
  if (bonus.category === 'activity-workout-points') return isActivityType(bonus.activity)
    && Number.isSafeInteger(bonus.points) && (bonus.points as number) > 0;
  if ((value as GearBonus).category !== 'activity-workout-points-bundle' || !Array.isArray(bonus.bonuses)
    || bonus.bonuses.length < 2 || bonus.bonuses.length > 3) return false;
  const activities = new Set<ActivityType>();
  for (const entry of bonus.bonuses) {
    if (!isGearBonus(entry) || entry.category !== 'activity-workout-points' || activities.has(entry.activity)) return false;
    activities.add(entry.activity);
  }
  return true;
}

/** Enumerate weighted draws, rather than estimating inclusion chances by sampling. */
export function gearActivityInclusionOdds(item: GearDefinition): WeightedOption<ActivityType>[] {
  const totals = new Map<ActivityType, number>();
  const categoryTotal = item.categories.reduce((sum, category) => sum + category.weight, 0);
  const countTotal = item.bonusCounts.reduce((sum, option) => sum + option.weight, 0);
  for (const category of item.categories) {
    for (const count of item.bonusCounts) {
      const visit = (remaining: readonly WeightedOption<ActivityType>[], selected: ActivityType[], probability: number): void => {
        if (selected.length === count.value) {
          selected.forEach((activity) => totals.set(activity, (totals.get(activity) ?? 0) + probability));
          return;
        }
        const total = remaining.reduce((sum, option) => sum + option.weight, 0);
        for (const option of remaining) visit(remaining.filter((entry) => entry.value !== option.value),
          [...selected, option.value], probability * option.weight / total);
      };
      visit(category.activities, [], category.weight / categoryTotal * count.weight / countTotal);
    }
  }
  return activityTypes.filter((activity) => totals.has(activity)).map((value) => ({ value, weight: totals.get(value)! }));
}

export function formatGearBonus(bonus: GearBonus): string {
  return getGearBonuses(bonus).map((entry) => `+${entry.points} points once per workout when you log ${entry.activity}`).join('; ');
}
export function validateGearLoadout(value: unknown, items: readonly GearInstance[]): string | null {
  if (!Array.isArray(value) || value.some((id) => typeof id !== 'string')) return 'Equipment must be a list of owned item IDs.';
  if (value.length > MAX_EQUIPPED_GEAR) return 'You can equip up to five items.';
  if (new Set(value).size !== value.length) return 'An item can only be equipped once.';
  const families = new Set<string>();
  for (const id of value) {
    const item = items.find((candidate) => candidate.id === id);
    if (!item) return 'You do not own that gear item.';
    if (families.has(item.familyId)) return 'Equip only one piece from each gear family.';
    families.add(item.familyId);
  }
  return null;
}
export function toggleGearItem(gear: UserGear, instanceId: string): string[] | null {
  const equipped = gear.equippedInstanceIds;
  if (equipped.includes(instanceId)) return equipped.filter((id) => id !== instanceId);
  const item = gear.items.find((candidate) => candidate.id === instanceId);
  if (!item) return null;
  const familyIndex = equipped.findIndex((id) => gear.items.find((candidate) => candidate.id === id)?.familyId === item.familyId);
  if (familyIndex !== -1) return equipped.map((id, index) => index === familyIndex ? instanceId : id);
  return equipped.length < MAX_EQUIPPED_GEAR ? [...equipped, instanceId] : null;
}
